import {
  type ForManagerLocationResponse,
  type getLocationsListInfiniteQueryOptions,
  type LocationDropDownContext,
  type locationsList,
  getLocationsListInfiniteQueryKey,
  getLocationsListQueryOptions,
  getLocationsListQueryKey,
  getLocationsRetrieveQueryKey,
  getNextPageParam,
  getStafferOrganizationsListQueryOptions,
  getUsersActiveLocationsListQueryKey,
  type LocationsListParams,
  locationsPartialUpdate,
  locationsRetrieve,
  type PatchedPlatformAdminLocationRequest,
  useLocationsList,
  useLocationsListInfinite,
  useLocationsPaymentMethodRetrieve,
  useLocationsRetrieve,
  useLocationsStatsRetrieve,
  useStafferOrganizationsList,
  usersActiveLocationsList,
  useUsersActiveLocationsList,
  type ForManagerLocationDetail,
  type LocationBillingInfo,
} from "@sidegigllc/api/client";
import type { ApiError } from "@sidegigllc/api/resources";
import { type InfiniteData, type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { useIsAdmin } from "domains/auth";
import { connectSkillAtom } from "domains/connect/state";
import { useAtomValue } from "jotai";
import React from "react";
import { useActiveLocationId, useActiveOrganization } from "services/locationOrgContext";
import type { QueryHook, QueryOptions } from "./types";

type LocationsPage = Awaited<ReturnType<typeof locationsList>>;

const managedListParams = (params?: LocationsListParams): LocationsListParams => ({
  query: "managed",
  ordering: "created_ts",
  ...params,
});

export const invalidateLocationLists = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: getLocationsListQueryKey() }),
    queryClient.invalidateQueries({ queryKey: getLocationsListInfiniteQueryKey() }),
  ]);

// prefill the retrieve cache with locations from the list query
export const usePrefillLocationCache = (locations?: Array<{ id: number }>) => {
  const queryClient = useQueryClient();
  React.useEffect(() => {
    for (const location of locations ?? []) {
      const queryKey = getLocationsRetrieveQueryKey(location.id);
      queryClient.setQueryData(queryKey, { ...queryClient.getQueryData(queryKey), ...location });
    }
  }, [locations, queryClient]);
};

export const seedCreatedLocationCaches = (queryClient: QueryClient, response: ForManagerLocationResponse) => {
  queryClient.setQueryData(getLocationsRetrieveQueryKey(response.id), response);
  queryClient.setQueryData<Array<LocationDropDownContext>>(getUsersActiveLocationsListQueryKey("me"), (prev) => {
    const existing = prev ?? [];
    if (existing.some((location) => location.id === response.id)) return existing;
    const row: LocationDropDownContext = {
      id: response.id,
      name: response.name,
      organization_id: response.organization_id,
      image_public_id: response.image_public_id,
      timezone: response.timezone,
    };
    return [...existing, row];
  });
};

export const locationsPageQueryOptions = (params: LocationsListParams) =>
  getLocationsListQueryOptions<LocationsPage>(managedListParams(params));

export const useLocationsPaginated = (
  params: LocationsListParams,
  options?: Parameters<typeof getLocationsListQueryOptions<LocationsPage>>[1],
) => {
  const query = useLocationsList<LocationsPage>(managedListParams(params), options);
  usePrefillLocationCache(query.data?.results);
  return query;
};

export const useLocationsInfinite = (
  params?: LocationsListParams,
  options?: Parameters<typeof getLocationsListInfiniteQueryOptions<InfiniteData<LocationsPage, number>>>[1],
) => {
  const query = useLocationsListInfinite<InfiniteData<LocationsPage, number>>(managedListParams(params), {
    ...options,
    query: { getNextPageParam, ...options?.query },
  });
  usePrefillLocationCache(query.data?.pages.at(-1)?.results);
  return query;
};

export const locationQueryOptions = (locationId: number) => ({
  queryKey: getLocationsRetrieveQueryKey(locationId),
  queryFn: (): Promise<ForManagerLocationDetail> => locationsRetrieve(locationId),
  staleTime: Number.POSITIVE_INFINITY,
});

// Always refetches the detail: the shared retrieve cache is prefilled from list rows (`staleTime: Infinity`), which carry no Cadence link fields.
export const useCadenceLink = (locationId: number) =>
  useLocationsRetrieve<{ linked: boolean; consoleUrl: string }, ApiError>(locationId, {
    query: {
      staleTime: 0,
      enabled: locationId > 0,
      select: (location: ForManagerLocationDetail) => ({
        linked: location.linked_to_cadence === true,
        consoleUrl: location.cadence_console_url ?? "",
      }),
    },
  });

export const activeLocationsQueryOptions = () => ({
  queryKey: getUsersActiveLocationsListQueryKey("me"),
  queryFn: () => usersActiveLocationsList("me"),
  staleTime: Number.POSITIVE_INFINITY,
});

export const organizationsQueryOptions = () =>
  getStafferOrganizationsListQueryOptions({ managed: true }, { query: { staleTime: Number.POSITIVE_INFINITY } });

// Route schema defaults locationId to the -1 sentinel on parse failure; treat as "no id".
export const useLocation = ({ id }: { id?: number }, options?: QueryOptions<ForManagerLocationDetail>) =>
  useLocationsRetrieve<ForManagerLocationDetail, ApiError>(id ?? -1, {
    query: { staleTime: Number.POSITIVE_INFINITY, ...options, enabled: (options?.enabled ?? true) && !!id && id > 0 },
  });

export function useLocationUpdate() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["MUTATE_LOCATION"],
    mutationFn: (location: Partial<ForManagerLocationDetail>) =>
      locationsPartialUpdate(location.id!, location as PatchedPlatformAdminLocationRequest),
    onSuccess: () => invalidateLocationLists(queryClient),
  });
  return mutation;
}

export const useBillingInfo: QueryHook<LocationBillingInfo, { id?: number }> = ({ id }, options) =>
  useLocationsPaymentMethodRetrieve<LocationBillingInfo, ApiError>(id ?? -1, undefined, {
    query: { ...options, enabled: (options?.enabled ?? true) && !!id && id > 0 },
  });

type UseContextLocationParams = {
  includeAll?: boolean;
};

export const useContextLocations = ({ includeAll }: UseContextLocationParams = {}) => {
  const activeOrgId = useActiveOrganization()?.id;

  const allLocations = useUsersActiveLocationsList("me", undefined, {
    query: { staleTime: Number.POSITIVE_INFINITY },
  });

  const orgs = useStafferOrganizationsList({ managed: true }, { query: { staleTime: Number.POSITIVE_INFINITY } });

  return {
    ...allLocations,
    data:
      !includeAll && activeOrgId
        ? allLocations.data?.filter((location) => location.organization_id === activeOrgId)
        : allLocations.data,
    refetch: () => Promise.all([allLocations.refetch(), orgs.refetch()]),
  };
};

export function useIsFirstGigPost() {
  const isAdmin = useIsAdmin();

  const [activeLocationId] = useActiveLocationId();
  const { data: locations } = useContextLocations();

  const hasConnectData = !!useAtomValue(connectSkillAtom);

  const activeLocationStats = useLocationsStatsRetrieve(activeLocationId ?? -1, {
    query: {
      enabled: hasConnectData && !!activeLocationId && locations?.length === 1 && !isAdmin,
      staleTime: Number.POSITIVE_INFINITY,
    },
  });

  return (
    !isAdmin &&
    hasConnectData &&
    locations?.length === 1 &&
    !!activeLocationStats?.data &&
    !activeLocationStats?.data?.has_posted_first_gig
  );
}
